import { useCallback } from 'react';
import { ShowPageLayout } from 'framework/ui/layout';
import { useParams } from 'react-router-dom';
import { FormShowTemplate, DEFAULT_ERROR_MESSAGE, FormFieldsData } from 'framework/form';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { normalizeStoragePath, normalizeStoragePathWithSlash } from 'domain/shared/documentStorage';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { useDispatch, useSelector } from 'react-redux';
import { MapedPoliciesDataType, PoliciesDataType, ComplianceDocumentTypeFE } from 'domain/shared/compliance';
import ComplianceDocumentSignContent from './ComplianceDocumentSignContent';
import { NOTIFICATIONS_TYPES } from 'infrastructure/notification/constants';
import { setNotificationResponse } from 'infrastructure/notification/redux/actions';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { useEmployeeService, useEmployeeUrl } from 'domain/shared/employee';
import useEmployeeComplianceRegistryService from 'domain/people/employee/compliance/useEmployeeComplianceRegistryService';
import { selectPolicyNotifications } from '../shared/notifications/selectPolicyNotifications';
import { loadDiffSafe, useDifferenceByLinks } from 'framework/DiffViewer';
import { useFileDifferenceApi } from 'infrastructure/api';
import getPreviousPolicyPath from './helpers/getPreviousPolicyPath';

const ComplianceDocumentSignPage = () => {
  const currentAlias = useEmployeeId()!;
  const { documentPath, policyPath } = useParams();
  const { addMessageAndRedirect } = useMessageService();
  const { getById } = useEmployeeService();
  const { getDocumentByPaths, getDocumentToken } = useDocumentStorageContext();
  const { getDifference } = useDifferenceByLinks(useFileDifferenceApi);
  const { signComplianceDocument, search } = useEmployeeComplianceRegistryService();
  const { getSingleEntityUrl, getEmployeeComplianceUrl } = useEmployeeUrl();

  const dispatch = useDispatch();

  const complianceNotifications = useSelector(selectPolicyNotifications);
  const decodedDocumentPath = decodeURIComponent(documentPath || policyPath || '');

  const getShowData = async () => {
    try {
      const employee = await getById(currentAlias);
      const documentData = (await getDocumentByPaths([decodedDocumentPath]))[0] as ComplianceDocumentTypeFE;
      const complianceItems = (await search({ pageNumber: 0, pageSize: 100 })).result || [];

      const previousPolicyPath =
        getPreviousPolicyPath(
          decodedDocumentPath,
          complianceItems as MapedPoliciesDataType[],
          documentData.metadata.complianceName || '',
          Number(documentData.metadata.version || 0),
        ) || null;

      const getDiffToken = async (path: string) => await getDocumentToken(normalizeStoragePath(path));
      const diffResult = await loadDiffSafe(getDiffToken, getDifference, previousPolicyPath, decodedDocumentPath);

      return {
        nameEn: employee.nameEn,
        ...documentData,
        difference: diffResult.diff?.differences || [],
        documentPath: decodedDocumentPath,
      };
    } catch {
      return addMessageAndRedirect(new ErrorMessage(DEFAULT_ERROR_MESSAGE), getSingleEntityUrl('me'));
    }
  };

  const getUpdatedComplianceDocumentNotifications = useCallback(
    (signedDocumentPath: string) => {
      return complianceNotifications?.filter((item: PoliciesDataType) => item.policyPath !== signedDocumentPath);
    },
    [complianceNotifications],
  );

  const handleComplianceDocumentSign = useCallback(
    async ({ documentPath: formDocumentPath }: FormFieldsData) => {
      await signComplianceDocument({
        policyPath: normalizeStoragePathWithSlash(formDocumentPath),
        isPolicyAccepted: true,
        consentPageUrl: window.location.href,
      });

      dispatch(
        setNotificationResponse({
          type: NOTIFICATIONS_TYPES.POLICY,
          response: getUpdatedComplianceDocumentNotifications(formDocumentPath),
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getUpdatedComplianceDocumentNotifications, complianceNotifications],
  );

  return (
    <FormShowTemplate
      getDataFunc={getShowData}
      submitFormFunc={handleComplianceDocumentSign}
      afterSubmit={{
        redirect: getEmployeeComplianceUrl('me'),
        message: 'You are awesome! The document has been successfully signed.',
      }}
      mapDataForForm={({ documentPath: formDocumentPath }) => {
        return {
          documentPath: formDocumentPath,
          isPolicyAccepted: false,
        };
      }}
      getValidationSchemaFunc={async () => ({
        isPolicyAccepted: {
          type: 'boolean',
          required: true,
        },
      })}
      initEditMode
    >
      <ShowPageLayout hasSeparateSections>
        <ComplianceDocumentSignContent />
      </ShowPageLayout>
    </FormShowTemplate>
  );
};

export default ComplianceDocumentSignPage;
